import React, { useRef, useState } from "react";
import { Trash, CheckCircle, Camera, Receipt, UploadSimple } from "@phosphor-icons/react";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { CurrencySelect } from "@/components/CurrencySelect";
import { Card } from "@/components/common";
import { categoryChoicesFor, reconcileCategory, CATEGORY_LOCKED_HINT, isOffListCategory, offListCategoryHint } from "@/lib/categoryGate";
import { MoneyInput } from "@/lib/accountingFormat";
import { curCode, unreadableCurrencyMessage } from "@/lib/currencies";
import { AddCategoryInline, ADD_CATEGORY_OPTION, ADD_CATEGORY_LABEL, NO_FAMILY_CATEGORIES_HINT } from "@/components/AddCategoryInline";

const toNum = (v) => { const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, "")); return isNaN(n) ? "" : n; };

function Stepper({ step, source, queueCount }) {
  const steps = source === "file" ? ["Source", "Map columns", "Review & import"] : ["Upload receipts", "Review & import"];
  const cur = source === "file" ? step : (step >= 3 ? 2 : 1);
  return (
    <div className="mt-3 flex items-center gap-2 text-xs font-semibold">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <span className={`rounded-full px-2.5 py-1 ${i + 1 <= cur ? "bg-[#034AA2] text-white" : "bg-[#E9E9EB] text-[#6b7280]"}`}>
            {i + 1}. {s}{i === 0 && source !== "file" && queueCount > 0 ? ` (${queueCount})` : ""}
          </span>
          {i < steps.length - 1 && <span className="text-[#C2C9D1]">—</span>}
        </React.Fragment>
      ))}
    </div>
  );
}


// THE "SPLIT ACROSS TAX YEARS" PANEL IS GONE.
//
// It took ONE receipt with ONE payment date and let the reviewer chop it into
// several expenses under years typed by hand — one real payment, several
// invented years. The account owner ruled it out on 2026-08-16: "I do not want
// to offer something that is not best practice."
//
// The legitimate need it served badly — a receipt that genuinely LISTS several
// payments on several real dates — comes back properly as ZEN-178, split by
// payment date rather than by typed year, so each entry's year falls out of its
// own date. (ZEN-172)
// A PERMANENT LABEL ABOVE EVERY BOX, in the same shape the labelled review
// panel already uses — ZEN-513 names that panel as the thing to read before
// writing new ones, so this is its RRow with the same type, spacing and colour.
//
// THE ROOT CAUSE WAS NEVER THE WIDTH. Every box used to be labelled by
// placeholder text, and placeholder text disappears the moment a value is
// present — which it always is here, because the reader is reviewing filled
// rows. So by the time a person looked, nothing on screen said what anything
// was. "Expertise Physio" — is that who was paid, or what it was for?
function Field({ label, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#6b7280]">{label}</span>
      {children}
    </label>
  );
}

function ReviewRow({ d, students, currencies, onChange, onRemove, isStudentLocked }) {
  // The category list belongs to THIS row's beneficiary, not to whoever the
  // import was started for — a row's beneficiary can still be changed above.
  const stu = students.find((s) => s.id === d.student_id);
  const expenseGroup = (stu?.schools || []).filter((s) => !s.archived)[0]?.expense_group || "post_secondary";
  const familyCategories = stu?.noneligible_categories || [];
  const choices = categoryChoicesFor({ eligible: d.eligible_529, expenseGroup, familyCategories });
  // ADDING A CATEGORY OPENS THE SHARED BOX INSIDE THIS ROW. (ZEN-512) It used
  // to open Settings in a second tab: the batch was left behind on the first
  // one and the new category was never chosen. Nothing leaves the page now, so
  // every other row keeps what was typed on it.
  const [addCatOpen, setAddCatOpen] = useState(false);
  // What the row says NOW, read when the save answers: a reply for a
  // beneficiary or a 529 answer the row has since left chooses nothing.
  const rowNow = useRef(d);
  rowNow.current = d;

  // Answering the 529 question is what unlocks the category, so it also decides
  // what happens to one already sitting on the row. A category the newly chosen
  // list still contains is kept; anything else goes back to the placeholder,
  // because it came from the other list. The receipt reading's own suggestion
  // is held in _suggested_category and re-offered here rather than shown before
  // the question — so answering does not throw the reading's work away, and
  // un-answering does not lose it either. (ZEN-282)
  const answerEligibility = (next) => {
    const candidate = d.category || d._suggested_category || "";
    onChange({
      eligible_529: next,
      category: reconcileCategory({ category: candidate, eligible: next, expenseGroup, familyCategories }),
    });
  };

  // AND THE SAME RECONCILIATION WHEN THE ROW'S BENEFICIARY CHANGES. The
  // beneficiary decides WHOSE lists these are — their education group selects
  // the IRS one, and the non-529 list is kept per beneficiary. Moving a row
  // from a Higher Education beneficiary to a K-12 one used to leave, say,
  // "Room & Board" on the row: absent from the new dropdown, so invisible, and
  // still sent on commit. (ZEN-282)
  const changeStudent = (nextId) => {
    const nextStu = students.find((s) => s.id === nextId);
    const nextGroup = (nextStu?.schools || []).filter((s) => !s.archived)[0]?.expense_group || "post_secondary";
    const nextFamily = nextStu?.noneligible_categories || [];
    onChange({
      student_id: nextId,
      category: reconcileCategory({ category: d.category, eligible: d.eligible_529, expenseGroup: nextGroup, familyCategories: nextFamily }),
    });
  };

  // A spreadsheet cell that was not a currency leaves this row with none until
  // the family picks one. (ZEN-829)
  const needsCurrency = !curCode(d.original_currency) && !!d._unreadable_currency;

  return (
    <Card className={`!p-3 ${needsCurrency ? "!border-2 !border-[#F01919]" : ""}`} data-testid="import-row">
      {needsCurrency && (
        <p className="mb-2 text-xs font-semibold text-[#F01919]" data-testid="row-cur-unreadable">{unreadableCurrencyMessage(d._unreadable_currency)}</p>
      )}
      {/* The eligibility question stays ABOVE the fields, where it was: it
          decides which category list the row may use, so it is asked first. */}
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-xs font-bold text-[#374151]">Is this 529-eligible?</span>
        <label className="flex items-center gap-1.5 text-xs font-medium"><input type="checkbox" checked={d.eligible_529 === true} onChange={(e) => answerEligibility(e.target.checked ? true : null)} data-testid="row-eligible" className="accent-[#077A39]" /> 529-eligible</label>
        <label className="flex items-center gap-1.5 text-xs font-medium"><input type="checkbox" checked={d.eligible_529 === false} onChange={(e) => answerEligibility(e.target.checked ? false : null)} data-testid="row-not-eligible" /> Not 529-eligible</label>
        {d.eligible_529 !== true && d.eligible_529 !== false && <span className="rounded-full bg-[#FEF3C7] px-1.5 py-0.5 text-[10px] font-semibold text-[#7D3306]" data-testid="row-eligible-undecided">Decide later (saved as draft)</span>}
      </div>

      {/* ROW ONE — WHAT HAPPENED. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Date paid">
          <UnifiedDateInput value={d.transaction_date} onChange={(v) => onChange({ transaction_date: v })} testid="row-date" hideHint />
        </Field>
        <Field label="Paid to">
          <input value={d.payee} onChange={(e) => onChange({ payee: e.target.value })} className="ri" data-testid="row-payee" />
        </Field>
        <Field label="What it was for">
          <input value={d.description} onChange={(e) => onChange({ description: e.target.value })} className="ri" data-testid="row-desc" />
        </Field>
      </div>

      {/* ROW TWO — HOW IT FILES. */}
      <div className="mt-2 grid grid-cols-1 items-end gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Category">
          {/* NEITHER LIST UNTIL THE QUESTION IS ANSWERED — same rule as the
              classify dialog, kept in one place now. (ZEN-282 / ZEN-114) */}
          {choices === null ? (
            <span className="ri flex items-center border-dashed bg-[#F8FAFC] text-[11px] leading-tight text-[#6b7280]" data-testid="row-cat-locked">{CATEGORY_LOCKED_HINT}</span>
          ) : (
            <select value={d.category || ""} onChange={(e) => {
              const v = e.target.value;
              // Opens the box instead of choosing something, so it writes
              // nothing to the row; the picker snaps back to what it held.
              if (v === ADD_CATEGORY_OPTION) { setAddCatOpen(true); return; }
              onChange({ category: v });
            }} className={`ri ${d.category ? "" : "text-[#9CA3AF]"}`} data-testid="row-cat">
              {!d.category && <option value="" disabled>Select a category</option>}
              {choices.map((c) => <option key={c} value={c} className="text-[#1f2937]">{c}</option>)}
              {/* The row's own category when the list no longer offers it, so
                  the picker never shows a different one. Shared rule. (ZEN-372) */}
              {isOffListCategory(d.category, choices) && <option value={d.category} className="text-[#1f2937]">{d.category}</option>}
              {d.eligible_529 === false && <option value={ADD_CATEGORY_OPTION}>{ADD_CATEGORY_LABEL}</option>}
            </select>
          )}
          {choices !== null && isOffListCategory(d.category, choices) && (
            <div className="mt-0.5 text-[10px] font-semibold text-[#034AA2]" data-testid="row-cat-custom-hint">{offListCategoryHint(d.category)}</div>
          )}
        </Field>
        <Field label="Currency">
          <CurrencySelect value={d.original_currency} onChange={(v) => onChange({ original_currency: v })} testid="row-cur" className={`!h-9 ${needsCurrency ? "!border-2 !border-[#F01919]" : ""}`} />
        </Field>
        <Field label="Amount">
          <MoneyInput currency={curCode(d.original_currency)} size="sm" className="!h-9" value={d.original_amount} onChange={(v) => onChange({ original_amount: v })} testid="row-amount" />
        </Field>
        <Field label="Whose expense">
          {isStudentLocked ? (
            <span className="ri flex items-center text-xs font-semibold text-[#6b7280]" data-testid="row-student-locked">{students.find((s) => s.id === d.student_id)?.full_name?.split(" ")[0] || "—"}</span>
          ) : (
            <select value={d.student_id} onChange={(e) => changeStudent(e.target.value)} className="ri" data-testid="row-student">{students.map((s) => <option key={s.id} value={s.id}>{s.nickname || s.full_name.split(" ")[0]}</option>)}</select>
          )}
        </Field>
      </div>

      {addCatOpen && d.eligible_529 === false && stu && (
        <AddCategoryInline
          key={stu.id}
          student={stu}
          testid="row-cat-add"
          isStillWanted={(sid) => rowNow.current.student_id === sid && rowNow.current.eligible_529 === false}
          onCancel={() => setAddCatOpen(false)}
          onChosen={(stored) => { onChange({ category: stored }); setAddCatOpen(false); }} />
      )}

      {/* THE FOOTER, and Remove sits at the far end of it — OWNER DECISION
          2026-09-02. It used to be a bare bin icon at the end of the field row,
          within a mis-click of the boxes a person is typing into, and a bare
          icon makes the reader guess what it does. Nothing destructive belongs
          among the fields, and it says what it does. */}
      <div className="mt-2 flex items-center gap-3 border-t border-[#E9E9EB] pt-2">
        {d.eligible_529 === false && familyCategories.length === 0 && (
          <span className="text-[11px] font-semibold text-[#B45309]" data-testid="row-no-family-cats">{NO_FAMILY_CATEGORIES_HINT}</span>
        )}
        <span className="text-xs text-[#6b7280]" data-testid="row-tax-year">
          Counts toward tax year {(d.transaction_date || "").slice(0, 4) || "—"}
        </span>
        <button onClick={onRemove} className="ml-auto flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold text-[#6b7280] hover:bg-[#E9E9EB] hover:text-[#F01919]" data-testid="row-remove">
          <Trash size={14} weight="bold" /> Remove
        </button>
      </div>
      <style>{`.ri{height:36px;border:1px solid #C2C9D1;border-radius:6px;padding:0 8px;font-size:13px;background:#fff;width:100%}`}</style>
    </Card>
  );
}

export { Stepper, ReviewRow };
